import React, { useCallback, useEffect, useState } from 'react';
import { View, Image, StyleSheet, TouchableOpacity, ActivityIndicator } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { Typography } from '../ui';
import { useTheme } from '../../context/ThemeContext';
import { api, getFileUrl } from '../../services/api';
import { formatProgramPrice } from '../dashboard/programsFormat';
import {
    describePurchase,
    formatPurchaseDate,
    productLabel,
    shouldRenderSection,
    summaryLine,
    type PurchaseTone,
} from './purchaseHistory';
import {
    canRequestRefund,
    latestRefundByPurchase,
    isRefundClosed,
    providerStatusNote,
    refundBlockedReason,
    refundStatusLabel,
    refundStatusNote,
} from '../dashboard/programs/programLifecycle';
import type { ProgramRefundRequest } from '../../types/programLifecycle';
import type { PurchaseHistoryItem } from '../../types/programs';

/**
 * CORE-880 — блок «Покупки» в финансовом разделе пользователя.
 *
 * Здесь единая история того, что человек купил у всех мастеров: одна строка на
 * платёж, включая незавершённые и неудачные. Библиотека купленного — другое
 * место (`Профиль → Программы`, CORE-886), и полноценного плеера программы тут
 * быть не должно: финансовый экран отвечает на вопрос «за что я заплатил»,
 * а не «где мне это смотреть».
 *
 * Ни одна сумма здесь не вычисляется. Всё, что показано, — снимки, взятые
 * покупкой в момент оформления; фронт их только форматирует.
 */

const PAGE_SIZE = 20;

export interface PurchasesSectionProps {
    /**
     * Переход в купленную программу.
     *
     * Необязательный намеренно. Пользовательской вкладки «Программы» ещё нет
     * (CORE-886), и вести человека некуда — кнопка, ведущая в никуда, хуже её
     * отсутствия. Как только экран появится, его хватит подключить сюда одной
     * строкой: остальная работа уже сделана.
     */
    onOpenProgram?: (item: PurchaseHistoryItem) => void;
}

export const PurchasesSection: React.FC<PurchasesSectionProps> = ({ onOpenProgram }) => {
    const { colors: C } = useTheme();

    const [items, setItems] = useState<PurchaseHistoryItem[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [pendingCount, setPendingCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);

    // CORE-882 — заявки на возврат по покупкам этого человека. Отдельным
    // запросом, а не полем покупки: заявка живёт своей жизнью и меняет статус
    // без участия покупки.
    const [refunds, setRefunds] = useState<Record<number, ProgramRefundRequest>>({});
    const [refundBusy, setRefundBusy] = useState<number | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setFailed(false);
        try {
            const [res, refundList] = await Promise.all([
                api.getPurchaseHistory(PAGE_SIZE, 0),
                // Сбой списка заявок не должен уронить покупки: заявки —
                // дополнение к истории, а не сама история.
                api.getMyRefundRequests(50).catch(() => ({ items: [], totalCount: 0 })),
            ]);
            setItems(res.items ?? []);
            setTotalCount(res.totalCount ?? 0);
            setPendingCount(res.pendingCount ?? 0);
            // CORE-880 (QA FAIL 08.09) — по покупке берём ПОСЛЕДНЮЮ заявку.
            // Прежняя сборка через `Object.fromEntries` оставляла последнюю пару
            // с тем же ключом, а список приходит новыми сверху — то есть
            // побеждала самая старая заявка, и рядом с «Деньги возвращены»
            // показывался отказ по прошлой.
            setRefunds(latestRefundByPurchase(refundList.items));
        } catch {
            // Сбой раздела не должен уронить финансовый экран целиком: рядом
            // живут другие блоки, и они к покупкам отношения не имеют.
            setFailed(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { void load(); }, [load]);

    const loadMore = useCallback(async () => {
        if (loadingMore || items.length >= totalCount) return;
        setLoadingMore(true);
        try {
            const res = await api.getPurchaseHistory(PAGE_SIZE, items.length);
            setItems((prev) => [...prev, ...(res.items ?? [])]);
        } catch {
            // Молча: уже показанное остаётся на месте, а повторить можно тем же
            // нажатием. Ошибка дозагрузки — не повод прятать загруженное.
        } finally {
            setLoadingMore(false);
        }
    }, [items.length, loadingMore, totalCount]);

    /**
     * Попросить вернуть деньги.
     *
     * Без формы причины: заявку разбирает человек, и первый шаг — сказать, что
     * она есть. Причину покупатель допишет в переписке, если её спросят;
     * требовать её сразу значит поставить преграду перед тем, кто и так
     * недоволен.
     */
    const requestRefund = useCallback(async (purchaseId: number) => {
        setRefundBusy(purchaseId);
        try {
            const req = await api.requestProgramRefund(purchaseId);
            setRefunds((prev) => ({ ...prev, [purchaseId]: req }));
        } catch (e) {
            // CORE-2071: сервер закрыл возврат правилом (начал заниматься,
            // событие началось), пока экран был открыт. Кнопку убираем и
            // показываем причину. Прочие ошибки — тихо: повторить можно тем
            // же нажатием.
            const code = (e as { code?: unknown } | null)?.code;
            if (code === 'REFUND_PRODUCT_USED' || code === 'REFUND_EVENT_STARTED') {
                setItems((prev) => prev.map((p) =>
                    p.purchaseId === purchaseId ? { ...p, refundBlockedCode: code } : p));
            }
        } finally {
            setRefundBusy(null);
        }
    }, []);

    const toneColor = (tone: PurchaseTone): string => {
        switch (tone) {
            case 'active': return C.success;
            case 'pending': return C.warning;
            case 'failed': return C.error;
            default: return C.textMuted;
        }
    };

    if (loading) {
        return (
            <View style={[s.section, s.centered]}>
                <ActivityIndicator color={C.textMuted} />
            </View>
        );
    }

    if (failed) {
        return (
            <View style={s.section}>
                <Typography variant="h3" style={[s.title, { color: C.textPrimary }]}>Покупки</Typography>
                <Typography variant="body" style={{ color: C.textSecondary }}>
                    Не получилось загрузить историю покупок.
                </Typography>
                <TouchableOpacity
                    onPress={() => void load()}
                    accessibilityRole="button"
                    style={[s.retry, { borderColor: C.border }]}
                >
                    <Typography variant="body" style={{ color: C.textPrimary }}>Попробовать снова</Typography>
                </TouchableOpacity>
            </View>
        );
    }

    // Ничего не куплено — раздела нет вовсе. Пустое состояние здесь объясняло бы
    // человеку, что он ничего не покупал, — он и так это знает.
    if (!shouldRenderSection(totalCount)) return null;

    const summary = summaryLine(totalCount, pendingCount);

    return (
        <View style={s.section} testID="purchasesSection">
            <View style={s.head}>
                <Typography variant="h3" style={[s.title, { color: C.textPrimary }]}>Покупки</Typography>
                {summary !== '' && (
                    <Typography variant="caption" style={{ color: C.textMuted }}>{summary}</Typography>
                )}
            </View>

            <View style={s.list}>
                {items.map((item) => {
                    const view = describePurchase(item);
                    const cover = item.coverImageUrl ? getFileUrl(item.coverImageUrl) : null;
                    const canOpen = view.canOpen && !!onOpenProgram;
                    const refund = refunds[item.purchaseId] ?? null;
                    const refundAllowed = canRequestRefund(item, refund);
                    const refundBlocked = refundBlockedReason(item, refund);

                    return (
                        <View
                            key={item.purchaseId}
                            style={[s.card, { backgroundColor: C.backgroundSoft, borderColor: C.borderSubtle }]}
                        >
                            <View style={s.row}>
                                {cover ? (
                                    <Image source={{ uri: cover }} style={s.cover} />
                                ) : (
                                    <View style={[s.cover, s.coverEmpty, { backgroundColor: C.backgroundAlt }]}>
                                        <Ionicons name="albums-outline" size={20} color={C.textMuted} />
                                    </View>
                                )}

                                <View style={s.main}>
                                    <Typography variant="caption" style={{ color: C.textMuted }}>
                                        {productLabel(item)}
                                    </Typography>
                                    <Typography variant="body" style={[s.name, { color: C.textPrimary }]}>
                                        {item.title || 'Без названия'}
                                    </Typography>
                                    {!!item.masterName && (
                                        <Typography variant="caption" style={{ color: C.textSecondary }}>
                                            {item.masterName}
                                        </Typography>
                                    )}
                                </View>

                                <View style={s.money}>
                                    <Typography variant="body" style={[s.amount, { color: C.textPrimary }]}>
                                        {formatProgramPrice(item.amount, item.currency)}
                                    </Typography>
                                    <Typography variant="caption" style={{ color: C.textMuted }}>
                                        {formatPurchaseDate(item.paidAtUtc ?? item.createdAtUtc)}
                                    </Typography>
                                </View>
                            </View>

                            <View style={[s.statusRow, { borderTopColor: C.borderSubtle }]}>
                                <View style={s.statusText}>
                                    <Typography variant="caption" style={{ color: toneColor(view.tone) }}>
                                        {view.statusLabel}
                                    </Typography>
                                    {view.note !== '' && (
                                        <Typography variant="caption" style={{ color: C.textMuted }}>
                                            {view.note}
                                        </Typography>
                                    )}
                                </View>

                                {refundAllowed && (
                                    <TouchableOpacity
                                        onPress={() => void requestRefund(item.purchaseId)}
                                        disabled={refundBusy === item.purchaseId}
                                        accessibilityRole="button"
                                        testID={`purchaseRefund-${item.purchaseId}`}
                                        style={[s.cta, { borderColor: C.border }]}
                                    >
                                        <Typography variant="caption" style={{ color: C.textMuted }}>
                                            {refundBusy === item.purchaseId ? 'Отправляем…' : 'Вернуть деньги'}
                                        </Typography>
                                    </TouchableOpacity>
                                )}

                                {canOpen && (
                                    <TouchableOpacity
                                        onPress={() => onOpenProgram?.(item)}
                                        accessibilityRole="button"
                                        style={[s.cta, { borderColor: C.border }]}
                                    >
                                        <Typography variant="caption" style={{ color: C.textPrimary }}>
                                            Перейти к программе
                                        </Typography>
                                    </TouchableOpacity>
                                )}
                            </View>

                            {refundBlocked !== '' && (
                                <View
                                    style={[s.refund, { borderTopColor: C.borderSubtle }]}
                                    testID={`purchaseRefundBlocked-${item.purchaseId}`}
                                >
                                    <Typography variant="caption" style={{ color: C.textMuted }}>
                                        {refundBlocked}
                                    </Typography>
                                </View>
                            )}

                            {refund && (
                                <View style={[s.refund, { borderTopColor: C.borderSubtle }]}>
                                    <Typography
                                        variant="caption"
                                        style={{ color: isRefundClosed(refund.status) ? C.textMuted : C.warning }}
                                    >
                                        {refundStatusLabel(refund.status)}
                                    </Typography>
                                    <Typography variant="caption" style={{ color: C.textMuted }}>
                                        {refundStatusNote(refund)}
                                    </Typography>
                                    {providerStatusNote(refund.providerStatus) !== '' && (
                                        <Typography variant="caption" style={{ color: C.textMuted }}>
                                            {providerStatusNote(refund.providerStatus)}
                                        </Typography>
                                    )}
                                </View>
                            )}
                        </View>
                    );
                })}
            </View>

            {items.length < totalCount && (
                <TouchableOpacity
                    onPress={() => void loadMore()}
                    disabled={loadingMore}
                    accessibilityRole="button"
                    style={[s.more, { borderColor: C.border }]}
                >
                    <Typography variant="body" style={{ color: C.textPrimary }}>
                        {loadingMore ? 'Загружаем…' : 'Показать ещё'}
                    </Typography>
                </TouchableOpacity>
            )}
        </View>
    );
};

const s = StyleSheet.create({
    section: { marginTop: 40 },
    centered: { alignItems: 'center', paddingVertical: 24 },
    head: { flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, marginBottom: 16 },
    title: { fontWeight: '700' },
    list: { gap: 12 },
    card: { borderRadius: 20, borderWidth: 1, padding: 16 },
    row: { flexDirection: 'row', alignItems: 'flex-start', gap: 12 },
    cover: { width: 56, height: 56, borderRadius: 12 },
    coverEmpty: { alignItems: 'center', justifyContent: 'center' },
    main: { flex: 1, gap: 2 },
    name: { fontWeight: '600' },
    money: { alignItems: 'flex-end', gap: 2 },
    amount: { fontWeight: '700' },
    statusRow: {
        flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
        gap: 12, marginTop: 14, paddingTop: 12, borderTopWidth: 1,
    },
    statusText: { flex: 1, gap: 2 },
    refund: { marginTop: 12, paddingTop: 10, borderTopWidth: 1, gap: 2 },
    cta: { paddingHorizontal: 14, paddingVertical: 8, borderRadius: 100, borderWidth: 1 },
    more: { marginTop: 16, alignSelf: 'center', paddingHorizontal: 20, paddingVertical: 10, borderRadius: 100, borderWidth: 1 },
    retry: { marginTop: 12, alignSelf: 'flex-start', paddingHorizontal: 16, paddingVertical: 8, borderRadius: 100, borderWidth: 1 },
});
